// Infrastructure i18n de l'app (i18next + react-i18next + expo-localization).
//
// - Français = langue principale ET fallback obligatoire.
// - Langue effective = préférence utilisateur (AsyncStorage 'app_language_v1',
//   'fr' | 'en' | absente = auto) sinon langue du device (expo-localization).
// - PAS de <I18nextProvider> nécessaire : on initialise l'instance GLOBALE
//   d'i18next (via initReactI18next) AVANT le premier rendu (app/_layout.tsx),
//   et react-i18next retombe automatiquement sur cette instance globale.
// - Pluriels : format JSON v4 (suffixes _one/_other). Si Hermes n'expose pas
//   Intl.PluralRules, i18next v26 retombe sur une règle interne
//   (count === 1 → 'one', sinon 'other'), correcte pour fr/en.
//
// Voir i18n/README.md pour les conventions (namespaces, clés, données métier).

import AsyncStorage from '@react-native-async-storage/async-storage';
import { requireOptionalNativeModule } from 'expo-modules-core';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import sûr : utils/supabase n'importe pas i18n (pas de cycle).
import { supabase } from '@/utils/supabase';

import en from './locales/en.json';
import fr from './locales/fr.json';

export type AppLanguage = 'fr' | 'en';

export const SUPPORTED_LANGUAGES: readonly AppLanguage[] = ['fr', 'en'];

/** Clé AsyncStorage de la préférence de langue ('fr' | 'en' ; absente = auto). */
export const LANGUAGE_STORAGE_KEY = 'app_language_v1';

const isAppLanguage = (value: unknown): value is AppLanguage =>
  value === 'fr' || value === 'en';

/** Langue du device si supportée, sinon fallback 'fr'.
 *
 * Le module natif ExpoLocalization est résolu via requireOptionalNativeModule
 * (expo-modules-core) qui renvoie null SANS JETER quand le build natif ne le
 * contient pas (dev build antérieur à l'ajout du module). Un import/require
 * classique de 'expo-localization' jette « Cannot find native module » — et
 * même attrapé, Metro logge l'erreur en rouge avant notre catch. Ici :
 * module absent → détection auto = 'fr', silencieusement ; le sélecteur
 * manuel FR|EN reste pleinement fonctionnel. Les builds à jour gardent la
 * détection complète de la langue du device. */
function detectDeviceLanguage(): AppLanguage {
  try {
    const native = requireOptionalNativeModule<{
      getLocales(): { languageCode: string | null }[];
    }>('ExpoLocalization');
    if (!native) return 'fr';
    const code = native.getLocales()[0]?.languageCode;
    return isAppLanguage(code) ? code : 'fr';
  } catch {
    return 'fr';
  }
}

/**
 * Préférence de langue stockée par l'utilisateur.
 * `null` = aucune préférence → suivre la langue du device (mode auto).
 */
export async function getAppLanguagePreference(): Promise<AppLanguage | null> {
  try {
    const stored = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isAppLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Initialise i18next (idempotent). À attendre AVANT le premier rendu React
 * (fait dans app/_layout.tsx, aux côtés du chargement des fonts).
 */
export async function initI18n(): Promise<void> {
  if (i18n.isInitialized) return;
  const preference = await getAppLanguagePreference();
  await i18n.use(initReactI18next).init({
    lng: preference ?? detectDeviceLanguage(),
    fallbackLng: 'fr',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    resources: {
      fr: { translation: fr },
      en: { translation: en },
    },
    interpolation: {
      // React échappe déjà les valeurs interpolées : pas de double échappement.
      escapeValue: false,
    },
    react: {
      // L'init est attendue avant le rendu : pas besoin de Suspense. Si un
      // composant montait quand même trop tôt, t() rend la clé au lieu de
      // suspendre l'arbre.
      useSuspense: false,
    },
  });
}

/**
 * Synchronise la langue effective vers `user_metadata.language` du compte
 * Supabase connecté (le cas échéant). Les templates d'emails d'auth GoTrue
 * choisissent leur langue via `.Data.language` ('en' → anglais, sinon
 * français). Best-effort : aucune erreur ne remonte (la langue des emails
 * ne doit jamais bloquer le changement de langue de l'app).
 */
async function syncLanguageToSupabase(language: AppLanguage): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
    await supabase.auth.updateUser({ data: { language } });
  } catch {
    // Hors-ligne / session expirée / API indisponible : tant pis, le
    // prochain changement de langue (ou signup) resynchronisera.
  }
}

/**
 * Change la langue de l'app et persiste la préférence.
 * `null` = revenir au mode auto (langue du device).
 */
export async function setAppLanguage(lang: AppLanguage | null): Promise<void> {
  try {
    if (lang) {
      await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } else {
      await AsyncStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  } catch {
    // Stockage indisponible : on change quand même la langue pour la session.
  }
  const effective = lang ?? detectDeviceLanguage();
  // Fire-and-forget : la synchro réseau ne doit pas retarder le changement
  // de langue à l'écran.
  void syncLanguageToSupabase(effective);
  await i18n.changeLanguage(effective);
}

export default i18n;
